/**
 * Utility for grouping consecutive media messages (Images/Videos)
 * based on mediaGroupId or a fixed time window.
 *
 * msgId format:
 *   group{<mediaGroupId>}-msg1,msg2,...  — server-assigned group ID
 *   group{timestamp}-msg1,msg2,...       — time-window group (no explicit ID)
 */

import { normalizeTimestamp } from '@/features/timeformatter/timeStamp';
import { getMessageSenderId } from '@/shared/utils/messageUtils';

const GROUP_WINDOW_SECONDS = Number(import.meta.env?.VITE_MEDIA_GROUP_WINDOW_SECONDS) || 30;
const GROUP_WINDOW_MS = GROUP_WINDOW_SECONDS * 1000;

export const isMediaMessage = msg => {
   if (!msg || msg.type === 'date-label' || msg.type === 'unread-separator' || msg.deleteStatus) return false;

   const msgType = msg.msgType || msg.msgBody?.message_type;
   return msgType === 'image' || msgType === 'video';
};

export const getMediaGroupId = msg => msg?.msgBody?.media?.mediaGroupId;

/**
 * Returns the representative "leaf" message for grouping comparison.
 * For a groupedMedia wrapper, uses the first (newest) child message.
 */
const getRepresentativeMessage = msg => {
   if (msg?.type === 'groupedMedia' && msg.messages?.length > 0) {
      return msg.messages[0];
   }
   return msg;
};

const hasCaptionText = msg => Boolean(msg?.msgBody?.media?.caption?.trim());

export const canGroupMessages = (msg1, msg2) => {
   const rep1 = getRepresentativeMessage(msg1);
   const rep2 = getRepresentativeMessage(msg2);

   if (!isMediaMessage(rep1) || !isMediaMessage(rep2)) return false;

   // 1. Same sender. In a group the SDK puts the group id in `fromUserId` (identical
   //    for every message) and the real sender in `publisherId`, so we resolve the
   //    true sender via getMessageSenderId rather than comparing fromUserId directly.
   if (getMessageSenderId({ type: 'chats', message: rep1 }) !== getMessageSenderId({ type: 'chats', message: rep2 }))
      return false;

   // 2. Captioned media never groups — each captioned bubble stands alone.
   //    The reducer calls this primitive directly when stitching incoming
   //    messages, so the caption check has to live here (not just in the
   //    top-level groupMediaMessages flush logic).
   if (hasCaptionText(rep1) || hasCaptionText(rep2)) return false;

   // 3. Strict mediaGroupId match (if present)
   const groupId1 = getMediaGroupId(rep1);
   const groupId2 = getMediaGroupId(rep2);

   if (groupId1 || groupId2) {
      return groupId1 === groupId2;
   }

   // 4. Fixed grouping window
   const t1 = normalizeTimestamp(rep1.timestamp);
   const t2 = normalizeTimestamp(rep2.timestamp);

   return Math.abs(t1 - t2) <= GROUP_WINDOW_MS;
};

/**
 * Groups a batch of messages.
 * @param {Array} messages - List of messages (Newest -> Oldest).
 * @returns {Array} Processed list with groupedMedia objects.
 */
export const buildGroupedMediaObject = rawMessages => {
   // Ensure every child has type:'message' so seen-status checks work correctly
   // Sort messages chronologically (oldest first) so they render left-to-right, top-to-bottom
   const messages = [...rawMessages]
      .map(m => (m.type === 'message' ? m : { ...m, type: 'message' }))
      .sort((a, b) => normalizeTimestamp(a.timestamp) - normalizeTimestamp(b.timestamp));

   const base = rawMessages[0]; // use the newest message for group-level metadata
   const ids = messages.map(m => m.editMessageId).join(',');
   const mediaGroupId = getMediaGroupId(base); // all children share the same groupId
   const totalFileSize = messages.reduce((sum, m) => sum + (m?.msgBody?.media?.file_size ?? 0), 0);

   // group{<mediaGroupId>}-<ids>  — for server-assigned groups
   // group{timestamp}-<ids>        — for time-window groups (no explicit ID)
   const groupKey = mediaGroupId ? `group{${mediaGroupId}}` : 'group{timestamp}';
   const msgId = `${groupKey}-${ids}`;

   return {
      chatType: base.chatType,
      createdAt: base.createdAt,
      timestamp: base.timestamp,
      deleteByStatus: base.deleteByStatus,
      deleteStatus: base.deleteStatus,
      fromUserId: base.fromUserId,
      publisherId: base.publisherId,
      fromUserJid: base.fromUserJid,
      metaData: base.metaData || {},
      editedStatus: 0,
      favouriteBy: '0',
      favouriteStatus: 0,
      msgId,
      editMessageId: msgId,
      mediaGroupId, // top-level shortcut so callers don't have to parse msgId
      userJid: base.userJid,
      type: 'groupedMedia',
      totalFileSize,
      messages,
   };
};

/**
 * Extract the mediaGroupId embedded in a groupedMedia's msgId.
 *
 * group{ABC123}-msg1,msg2,...   →  "ABC123"  (server-assigned ID)
 * group{timestamp}-msg1,msg2,... →  null      (time-window group)
 */
export const extractGroupMediaId = msgId => {
   const match = msgId?.match(/^group\{(.+?)\}-/);
   if (!match || match[1] === 'timestamp') return null;
   return match[1];
};

export const groupMediaMessages = messages => {
   if (!messages || messages.length === 0) return [];

   const result = [];
   let currentGroup = [];

   const flushGroup = () => {
      if (!currentGroup.length) return;
      result.push(currentGroup.length === 1 ? currentGroup[0] : buildGroupedMediaObject(currentGroup));
      currentGroup = [];
   };

   for (const msg of messages) {
      // Non-media or Media with Caption → always breaks the current group
      const hasCaption = msg?.msgBody?.media?.caption?.trim();
      if (!isMediaMessage(msg) || hasCaption) {
         flushGroup();
         result.push(msg);
         continue;
      }

      // Start a new group
      if (!currentGroup.length) {
         currentGroup.push(msg);
         continue;
      }

      // Compare against the FIRST message in the open group so the overall
      // group span stays inside the fixed window and avoids chain-grouping.
      if (canGroupMessages(currentGroup[0], msg)) {
         currentGroup.push(msg);
      } else {
         flushGroup();
         currentGroup.push(msg);
      }
   }

   flushGroup();
   return result;
};

/**
 * Re-segment a groupedMedia's children after a RECALL (delete-for-everyone),
 * SPLITTING the group at every recalled position. Each recalled child is kept
 * as a top-level placeholder ({ ...child, deleteStatus: 1 }) so it renders the
 * "You deleted this message" label between the two resulting groups.
 *
 * (Delete-for-me leaves no placeholder, so it does not split — it just rebuilds
 * the remaining children as a single group.)
 *
 * Children are stored oldest-first; the conversation array is newest-first, so
 * the returned segments are reversed to newest-first for direct flatMap.
 *
 * @param   {Array} children     group children, oldest-first
 * @param   {Set}   recalledSet  msgIds being recalled
 * @returns {Array} segments (groups / singles / recalled placeholders), newest-first
 */
export const splitGroupedMediaOnRecall = (children, recalledSet) => {
   const segments = [];
   let run = [];

   const flushRun = () => {
      if (!run.length) return;
      segments.push(run.length === 1 ? run[0] : buildGroupedMediaObject(run));
      run = [];
   };

   for (const child of children) {
      if (recalledSet.has(child.msgId)) {
         flushRun();
         segments.push({ ...child, deleteStatus: 1 });
      } else {
         run.push(child);
      }
   }
   flushRun();

   return segments.reverse();
};
